import { useMemo, useState } from 'react';
import HotelCheckin from './HotelCheckin';
import { useTheme } from './theme';

const stylesFor = (theme) => {
  const dark = theme === 'dark';

  return {
    page: {
      padding: 16,
      fontFamily: 'Arial',
      background: dark ? '#0f172a' : '#f8fafc',
      color: dark ? '#e5e7eb' : '#0f172a',
      minHeight: '100vh',
    },

    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      gap: 12,
      flexWrap: 'wrap',
      marginBottom: 10,
    },

    kicker: {
      fontSize: 12,
      color: dark ? '#94a3b8' : '#64748b',
      letterSpacing: 1,
      textTransform: 'uppercase',
    },

    h2: {
      margin: 0,
      fontSize: 24,
      color: dark ? '#f8fafc' : '#0f172a',
    },

    headerActions: {
      display: 'flex',
      gap: 8,
      alignItems: 'center',
      flexWrap: 'wrap',
    },

    btn: {
      padding: '8px 12px',
      borderRadius: 10,
      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
      background: dark ? '#111827' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      cursor: 'pointer',
    },

    btnDanger: {
      padding: '8px 12px',
      borderRadius: 10,
      border: `1px solid ${dark ? '#7f1d1d' : '#ef4444'}`,
      background: '#991b1b',
      color: 'white',
      cursor: 'pointer',
    },

    toggleWrap: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },

    toggleLabel: {
      fontSize: 12,
      color: dark ? '#cbd5e1' : '#475569',
    },

    card: {
      border: `1px solid ${dark ? '#1f2937' : '#e5e7eb'}`,
      borderRadius: 14,
      padding: 14,
      marginBottom: 12,
      background: dark ? 'rgba(17, 24, 39, 0.7)' : '#ffffff',
      boxShadow: dark
        ? '0 10px 24px rgba(0,0,0,0.25)'
        : '0 10px 24px rgba(15, 23, 42, 0.08)',
      maxWidth: 1000,
    },

    muted: {
      color: dark ? '#94a3b8' : '#64748b',
      fontSize: 12,
    },

    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: 10,
      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
      background: dark ? '#0b1220' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontSize: 14,
    },

    listWrap: {
      marginTop: 12,
      maxHeight: 520,
      overflow: 'auto',
      border: `1px solid ${dark ? 'rgba(148,163,184,0.2)' : '#e5e7eb'}`,
      borderRadius: 12,
    },

    listRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 12px',
      borderBottom: `1px solid ${dark ? 'rgba(148,163,184,0.12)' : '#f1f5f9'}`,
      gap: 12,
    },

    name: {
      fontWeight: 700,
      color: dark ? '#f8fafc' : '#0f172a',
    },

    sub: {
      fontSize: 12,
      color: dark ? '#94a3b8' : '#64748b',
      marginTop: 2,
    },

    banner: {
      padding: '10px 12px',
      borderRadius: 12,
      margin: '12px 0',
      background: dark ? 'rgba(251, 191, 36, 0.15)' : '#fff8d6',
      border: `1px solid ${dark ? 'rgba(251, 191, 36, 0.35)' : '#ecdca3'}`,
      color: dark ? '#fef3c7' : '#3a2f00',
      maxWidth: 1000,
    },
  };
};

export default function AdminActAs({ users }) {
  const [theme, setTheme] = useTheme();
  const S = stylesFor(theme);

  const [q, setQ] = useState('');
  const [actingAs, setActingAs] = useState(null); // { memberNumber, name }
  // Admin-only cutoff override. Starts OFF every session — must be deliberately flipped
  // on per Act-As so nobody bypasses cutoffs by accident.
  const [bypassCutoff, setBypassCutoff] = useState(false);

  const logout = () => {
    localStorage.removeItem('authenticatedUser');
    window.location.reload();
  };

  const list = useMemo(() => {
    const arr = Object.values(users).filter(
      (u) => !!u.memberNumber && !u.isChef && !u.isAdmin
    );
    if (!q.trim()) return arr.slice(0, 50);
    const needle = q.toLowerCase();
    return arr
      .filter(
        (u) =>
          (u.name || '').toLowerCase().includes(needle) ||
          (u.memberNumber || '').toString().includes(needle)
      )
      .slice(0, 50);
  }, [users, q]);

  const Header = () => (
    <div style={S.headerRow}>
      <div>
        <div style={S.kicker}>Admin</div>
        <h2 style={S.h2}>Act As Member</h2>
      </div>

      <div style={S.headerActions}>
        <div style={S.toggleWrap}>
          <span style={S.toggleLabel}>Theme</span>
          <button
            style={S.btn}
            onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
            title="Toggle light/dark"
          >
            {theme === 'dark' ? '🌙 Dark' : '☀️ Light'}
          </button>
        </div>

        <button style={S.btnDanger} onClick={logout}>
          Logout
        </button>
      </div>
    </div>
  );

  if (actingAs) {
    return (
      <div style={S.page}>
        <Header />

        <div style={S.banner}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <b>Acting as:</b> {actingAs.name} (#{actingAs.memberNumber})
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <label
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '6px 10px', borderRadius: 10,
                  background: bypassCutoff ? 'rgba(239,68,68,0.22)' : 'transparent',
                  border: `1px solid ${bypassCutoff ? '#ef4444' : 'rgba(148,163,184,0.35)'}`,
                  cursor: 'pointer', fontWeight: 700, fontSize: 13,
                }}
                title="Bypass all meal cutoff times for this session. Admin only."
              >
                <input
                  type="checkbox"
                  checked={bypassCutoff}
                  onChange={(e) => setBypassCutoff(e.target.checked)}
                />
                {bypassCutoff ? '⚠️ Cutoff override: ON' : 'Cutoff override'}
              </label>
              <button onClick={() => setActingAs(null)} style={S.btn}>
                Switch Member
              </button>
            </div>
          </div>
          {bypassCutoff && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              Meal-time cutoffs, Monday/Tuesday breakfast blocks, and special-weekend deadlines are <b>all bypassed</b> for this booking. Use sparingly — this is logged nowhere.
            </div>
          )}
        </div>

        {/* Reuse the exact member flow, just override memberNumber */}
        <HotelCheckin memberNumber={actingAs.memberNumber} bypassCutoff={bypassCutoff} />
      </div>
    );
  }

  return (
    <div style={S.page}>
      <Header />

      <div style={S.card}>
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 700 }}>Select a member to act as</div>
          <div style={S.muted}>
            Then use their normal check-in screen.
          </div>
        </div>

        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or member #"
          style={S.input}
        />

        <div style={S.listWrap}>
          {list.map((u) => (
            <div key={`${u.memberNumber}-${u.name}`} style={S.listRow}>
              <div style={{ minWidth: 0 }}>
                <div style={S.name}>{u.name}</div>
                <div style={S.sub}>#{u.memberNumber}</div>
              </div>

              <button onClick={() => setActingAs(u)} style={S.btn}>
                Act As
              </button>
            </div>
          ))}

          {list.length === 0 && (
            <div style={{ padding: 12, ...S.muted }}>No matches.</div>
          )}
        </div>
      </div>
    </div>
  );
}
